import { ColumnValue } from '@Components/ColumnValue';
import type { ApiInvoiceSnapshot } from '@Model';
import { EnumInvoiceType, getEnumInvoiceTypeLabel, invoiceTypes } from '@Model';
import {
    Badge,
    type BadgeVariations,
    type Column,
    isNullOrEmpty,
    type ITableWithStoredSettingsProps,
    renderDateTime,
    type Undefined,
} from '@synqo/frontend-component-library';
import { isDateBeforeToday } from '@utils/columnModificators';

export const initialInvoiceSnapshotSorting: Readonly<
    ITableWithStoredSettingsProps<ApiInvoiceSnapshot>['initialSorting']
> = {
    columnName: 'date',
    direction: 'desc',
};

/**
 * An invoice snapshot is cancelled when a cancellation (Storno) snapshot links back to it. The
 * backend maintains that link in both directions, mirroring the source invoices.
 * @ai-generated 2026-08-13T10:50:31+00:00 by malte.ulfers@tngtech.com using Claude Opus 5
 */
export function isCancelledInvoiceSnapshot(row: ApiInvoiceSnapshot): boolean {
    return !isNullOrEmpty(row.related_cancellation?.foreign_key);
}

// Client-side derived payment state — cannot be filtered or sorted server-side.
// Swap for a backend field on ApiInvoiceSnapshot once DEVCLD-2167's PaymentState follow-up lands.
type PaymentState = 'cancelled' | 'paid' | 'overdue' | 'open';

// @ai-generated 2026-08-17T00:00:00+00:00 by malte.ulfers@tngtech.com using Claude Opus 5
export function derivePaymentState(row: ApiInvoiceSnapshot): PaymentState | undefined {
    // A cancellation is not itself "Storniert" and owes nothing, so it carries no payment state.
    if (row.type === EnumInvoiceType.Cancellation) {
        return undefined;
    }
    if (isCancelledInvoiceSnapshot(row)) {
        return 'cancelled';
    }
    if (!isNullOrEmpty(row.payment_completed_at)) {
        return 'paid';
    }
    if (isDateBeforeToday(row.due_at)) {
        return 'overdue';
    }
    return 'open';
}

const paymentStateLabels: Record<PaymentState, string> = {
    cancelled: 'Storniert',
    paid: 'Bezahlt',
    overdue: 'Überfällig',
    open: 'Offen',
};

const paymentStateVariants: Record<PaymentState, BadgeVariations> = {
    cancelled: 'danger',
    paid: 'success',
    overdue: 'danger',
    open: 'default',
};

/** The payment state badge the list shows, or nothing for a cancellation. */
// @ai-generated 2026-09-30T03:36:37+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function renderPaymentStateBadge(snapshot: ApiInvoiceSnapshot) {
    const state = derivePaymentState(snapshot);
    if (state === undefined) {
        return '';
    }
    return <Badge variant={paymentStateVariants[state]}>{paymentStateLabels[state]}</Badge>;
}

export const baseInvoiceSnapshotColumns: readonly Readonly<Column<ApiInvoiceSnapshot>>[] = [
    { name: 'number', label: 'Nummer' },
    {
        name: 'type',
        label: 'Rechnungsart',
        filterVariant: 'select',
        filterOptions: invoiceTypes,
        renderCell: ({ cellData }) => getEnumInvoiceTypeLabel(cellData as EnumInvoiceType),
    },
    {
        name: 'payment_state',
        label: 'Zahlstatus',
        disableColumnFilter: true,
        disableColumnSort: true,
        renderCell: ({ rowData }) => renderPaymentStateBadge(rowData),
    },
    { name: 'total_gross_price', label: 'Brutto in €', type: 'currency' },
    { name: 'date', label: 'Datum', type: 'date', filterVariant: 'day-range' },
];

export const openInvoiceSnapshotColumns: Column<ApiInvoiceSnapshot>[] = [
    ...baseInvoiceSnapshotColumns,
    {
        name: 'due_at',
        label: 'Fällig am',
        type: 'date',
        filterVariant: 'day-range',
        renderCell: ({ cellData, rowData }) => {
            if (derivePaymentState(rowData) === 'overdue') {
                return (
                    <ColumnValue variant="danger" noIcon>
                        {renderDateTime(cellData as string, 'date')}
                    </ColumnValue>
                );
            }
            return renderDateTime(cellData as Undefined<string>, 'date');
        },
    },
];

export const invoiceSnapshotColumns: Column<ApiInvoiceSnapshot>[] = [
    ...baseInvoiceSnapshotColumns,
    { name: 'due_at', label: 'Fällig am', type: 'date', filterVariant: 'day-range' },
    {
        name: 'payment_completed_at',
        label: 'Bezahlt am',
        type: 'date',
        renderCell: ({ cellData }) => (
            <ColumnValue variant={(cellData as Undefined<string>)?.length ? 'success' : 'default'}>
                {renderDateTime(cellData as string, 'date')}
            </ColumnValue>
        ),
    },
];
